import MapboxDraw from "@mapbox/mapbox-gl-draw";
import convert from "convert-units";
import { Feature, MultiPolygon, Polygon } from "geojson";
import { sumBy } from "lodash";

import { bbox, difference, feature as toFeature, featureCollection, polygon as toPolygon } from "fond/turf";
import { Maybe, MultiProject, MultiProjectArea, MultiProjectAreaStatistic, SystemOfMeasurement, SystemsOfMeasurement } from "fond/types";
import { isMultiPolygon, isPolygon } from "fond/types/geojson";
import { BBox } from "fond/types/geometry";
import { toBBox } from "fond/utils";

export type AreaErrorType = "prem_count_exceeded" | "name_duplication";

/**
 * Prevents a polygon from extending beyond another polygon
 */
export const clipOutside = (
  polygon: Feature<Polygon | MultiPolygon>,
  boundaryPolygon: Feature<Polygon | MultiPolygon>
): Feature<Polygon | MultiPolygon> => {
  let diffPoly: Feature<Polygon | MultiPolygon> | null = difference(polygon, boundaryPolygon);

  diffPoly = diffPoly ? difference(polygon, diffPoly) : polygon;

  return { ...diffPoly, id: polygon.id } as Feature<Polygon | MultiPolygon>;
};

/**
 * Converts a MultiPolygon into multiple Polygon features
 */
export const uncombine = (feature: Feature<MultiPolygon | Polygon>): Feature<Polygon>[] => {
  if (isMultiPolygon(feature)) {
    const polygons = feature.geometry.coordinates.map((coord) => toPolygon(coord, { ...feature.properties }));
    return polygons;
  } else if (isPolygon(feature)) {
    return [feature];
  }
  return [];
};

/**
 * Updates a property value on a feature currently being drawn
 */
export const setFeatureProperty = (drawControl: MapboxDraw, id: string, property: string, value: any): void => {
  if (drawControl) {
    drawControl.setFeatureProperty(id, property, value);

    // We refresh the features to force a redraw
    drawControl.set(drawControl.getAll());
  }
};

/**
 * Generates a bounding box based on the projects subarea boundaries.
 * @returns A turf.bbox converted to the format used by mapboxgl.Map
 */
export const getAreaBoundingBox = (areas: MultiProjectArea[]): BBox | null => {
  // Create a feature collection for turf to use
  const collection = featureCollection(areas.map(({ Boundary }) => toFeature(Boundary)));
  const turfBBox = bbox(collection);

  // Convert to mapboxgl bbox
  return toBBox(turfBBox);
};

export const scoringPalette: Record<string | number, string> = {
  100: "#066E15",
  90: "#2D891D",
  80: "#55A525",
  70: "#7CC02C",
  60: "#A4DC34",
  50: "#CBF73C",
  40: "#FCFF4B",
  30: "#FBC044",
  20: "#FA803D",
  10: "#F94135",
  0: "#F8012E",
  NA: "grey",
};

export const scoringChipPalette: Record<string | number, { bg: string; fg: string }> = {
  100: { bg: "#E6F4E8", fg: "#066E15" },
  90: { bg: "#E8F5E4", fg: "#2D891D" },
  80: { bg: "#EDF7E5", fg: "#55A525" },
  70: { bg: "#F1FAE6", fg: "#4A7A1B" },
  60: { bg: "#F5FCE8", fg: "#5C7A14" },
  50: { bg: "#F9FDE8", fg: "#6B7A0F" },
  40: { bg: "#FEFEE8", fg: "#8A8A00" },
  30: { bg: "#FEF3E2", fg: "#B5801A" },
  20: { bg: "#FEE9DB", fg: "#C45A1A" },
  10: { bg: "#FEE2DF", fg: "#D12B20" },
  0: { bg: "#FDDDE3", fg: "#C80125" },
  NA: { bg: "#F0F0F0", fg: "#666666" },
};

export const getScoreChipColors = (score: number): { bg: string; fg: string } => {
  if (score > 100) return scoringChipPalette[100];
  if (score < 0) return scoringChipPalette.NA;

  const lowerScore = Math.floor(score / 10) * 10;
  const finalScore = Math.max(lowerScore, 0);

  return scoringChipPalette[finalScore];
};

export const getScoreColor = (score: number): string => {
  if (score > 100) return scoringPalette[100];
  if (score < 0) return scoringPalette.NA;

  // Get the nearest lower multiple of 10
  const lowerScore = Math.floor(score / 10) * 10;

  // Ensure the lower score is not less than 0
  const finalScore = Math.max(lowerScore, 0);

  return scoringPalette[finalScore];
};

export const mapboxScoring = [
  "step",
  ["to-number", ["get", "score"]],
  scoringPalette.NA,
  0,
  scoringPalette[0],
  10,
  scoringPalette[10],
  20,
  scoringPalette[20],
  30,
  scoringPalette[30],
  40,
  scoringPalette[40],
  50,
  scoringPalette[50],
  60,
  scoringPalette[60],
  70,
  scoringPalette[70],
  80,
  scoringPalette[80],
  90,
  scoringPalette[90],
  99,
  scoringPalette[100],
];

/**
 * Calculates an areas score based on the areas statistics & the weights the user has defined.
 *
 * @param inputs The importance weightings used to determine the score.
 * @param areaId ID of the area to be scored
 * @param statistics The statistics for all the subareas within the project.
 * @returns {number} The areas score.
 */
export const calculateScore = (inputs: MultiProject["AssessConfiguration"], areaId: string, statistics: MultiProjectAreaStatistic[]): number => {
  const stats = statistics.find(({ Area }) => Area.ID === areaId);
  if (!stats) return 0;

  // Extract the provider fiber coverage and address breakdown importance from the inputs
  const {
    ProviderFiberCoverageImportance: fiberCoverageImportance,
    AddressBreakdownImportance,
    ...rest
  } = inputs ?? {
    ProviderFiberCoverageImportance: {},
    AddressBreakdownImportance: {},
  };

  // Default address breakdown Sdu to 0.5 if not present
  const addressBreakdownImportance = { Sdu: 0.5, ...AddressBreakdownImportance };

  // Add default values if the assessConfiguration has not yet been defined or is missing values.
  const importance = {
    AddressDensityImportance: 0.5,
    AddressUnderservedProportionImportance: 0.5,
    FiberCoverageProportionImportance: 0.5,
    WeightedMedianIncomeImportance: 0.5,
    ...rest,
  };

  const sum = sumBy(Object.values(importance)) + sumBy(Object.values(fiberCoverageImportance)) + sumBy(Object.values(addressBreakdownImportance));
  if (sum === 0) return 0;

  let score = 0;
  score += ((importance.AddressDensityImportance ?? 0) / sum) * stats.AddressDensity.Rank;
  score += ((importance.AddressUnderservedProportionImportance ?? 0) / sum) * stats.AddressUnderservedProportion.Rank;
  score += ((importance.WeightedMedianIncomeImportance ?? 0) / sum) * stats.WeightedMedianIncome.Rank;
  score += ((addressBreakdownImportance.Sdu ?? 0) / sum) * stats.AddressBreakdown.Sdu.Rank;
  score += ((importance.AddressPathDensityImportance ?? 0) / sum) * stats.AddressPathDensity.Rank;

  // We want the score to decrease as fiber coverage increases, so subtract the rank from 1
  score += ((importance.FiberCoverageProportionImportance ?? 0) / sum) * (1 - stats.FiberCoverageProportion.Rank);

  Object.entries(fiberCoverageImportance).forEach(([provider, providerImportance]) => {
    // We want the score to decrease as coverage increases, so subtract the rank from 1
    score += (providerImportance / sum) * (1 - (stats.ProviderFiberCoverage[provider]?.Rank ?? 0));
  });

  return 100 * score;
};

export const convertPath = (value: number, systemOfMeasurement?: SystemOfMeasurement): number =>
  convert(value)
    .from("m")
    .to(systemOfMeasurement === SystemsOfMeasurement.Imperial ? "ft" : "m");

/** Convert a density having units X per square metres to X per mile² or X per kilometer² */
export const convertDensity = (value: number, systemOfMeasurement?: SystemOfMeasurement): number =>
  value * (systemOfMeasurement === SystemsOfMeasurement.Imperial ? convert(1).from("mi2").to("m2") : convert(1).from("km2").to("m2"));

/** Convert a path density having units X per metres to X per mile or X per kilometer */
export const convertPathDensity = (value: number, systemOfMeasurement?: SystemOfMeasurement): number =>
  value * (systemOfMeasurement === SystemsOfMeasurement.Imperial ? convert(1).from("mi").to("m") : convert(1).from("km").to("m"));

export const convertArea = (value: number, systemOfMeasurement?: SystemOfMeasurement): number =>
  convert(value)
    .from("m2")
    .to(systemOfMeasurement === SystemsOfMeasurement.Imperial ? "mi2" : "km2");

/**
 * Return an array of providers that have been marked as important in the multi project's assess configuration.
 */
export const getImportantFiberProviders = (multiProject: Maybe<MultiProject>): string[] => {
  if (!multiProject?.AssessConfiguration) return [];

  const providers = Object.entries(multiProject.AssessConfiguration.ProviderFiberCoverageImportance).map(([provider]) => provider);
  providers.sort();
  return providers;
};
